import React, { useCallback, useEffect, useState } from 'react';

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [creatorNameInput, setCreatorNameInput] = useState('');
  const [referralMessage, setReferralMessage] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [toggleBusyId, setToggleBusyId] = useState(null);
  const [payoutBatches, setPayoutBatches] = useState([]);
  const [payoutBusyCode, setPayoutBusyCode] = useState('');
  const [markPaidBusyId, setMarkPaidBusyId] = useState('');

  const authHeaders = useCallback((includeJson = false) => {
    const token = localStorage.getItem('token');
    return includeJson
      ? { 'Content-Type': 'application/json', 'x-auth-token': token }
      : { 'x-auth-token': token };
  }, []);

  const readApiResponse = useCallback(async (resp, fallbackMessage) => {
    const contentType = resp.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.error || data.msg || fallbackMessage);
      }
      return data;
    }

    const text = await resp.text().catch(() => '');
    if (!resp.ok) {
      const trimmed = text.trim();
      throw new Error(trimmed || `${fallbackMessage} (HTTP ${resp.status})`);
    }
    return {};
  }, []);

  const formatMoney = useCallback((cents, currency = 'usd') => {
    const normalized = Number.isFinite(cents) ? cents : 0;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
    }).format(normalized / 100);
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setReferralMessage('');
    try {
      const usersResp = await fetch('/api/admin/users', {
        credentials: 'same-origin',
        headers: authHeaders(),
      });
      const usersData = await readApiResponse(usersResp, 'Failed to fetch users.');
      setUsers(Array.isArray(usersData) ? usersData : (usersData.users || []));

      const referralsResp = await fetch('/api/admin/referrals', {
        credentials: 'same-origin',
        headers: authHeaders(),
      });
      if (referralsResp.ok) {
        const referralsData = await readApiResponse(referralsResp, 'Failed to fetch referral codes.');
        setReferrals(referralsData.referrals || []);
      } else {
        await readApiResponse(referralsResp, 'Referral codes are unavailable on this environment.');
        setReferrals([]);
      }

      const batchesResp = await fetch('/api/admin/referrals/payout-batches', {
        credentials: 'same-origin',
        headers: authHeaders(),
      });
      if (batchesResp.ok) {
        const batchesData = await readApiResponse(batchesResp, 'Failed to fetch payout batches.');
        setPayoutBatches(batchesData.batches || []);
      } else {
        setPayoutBatches([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, readApiResponse]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleAdmin = async (id, current) => {
    const confirmMsg = current ? 'Revoke admin from this user?' : 'Grant admin to this user?';
    if (!window.confirm(confirmMsg)) return;
    try {
      const resp = await fetch(`/api/admin/users/${id}/admin`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: authHeaders(true),
        body: JSON.stringify({ admin: !current }),
      });
      const data = await readApiResponse(resp, 'Failed to update admin flag');
      // update local users state
      setUsers((prev) => prev.map(u => u._id === id ? { ...u, admin: data.admin } : u));
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const createReferralCode = async (e) => {
    e.preventDefault();
    setReferralMessage('');
    const normalizedCode = referralCodeInput.trim().toUpperCase();
    const creatorName = creatorNameInput.trim();

    if (!normalizedCode || !creatorName) {
      setReferralMessage('Code and creator name are required.');
      return;
    }

    try {
      setReferralLoading(true);
      const resp = await fetch('/api/admin/referrals', {
        method: 'POST',
        credentials: 'same-origin',
        headers: authHeaders(true),
        body: JSON.stringify({ code: normalizedCode, creatorName }),
      });
      const data = await readApiResponse(resp, 'Failed to create referral code.');

      setReferralCodeInput('');
      setCreatorNameInput('');
      setReferralMessage(`Created referral code ${data.referral.code}.`);
      setReferrals((prev) => [data.referral, ...prev]);
    } catch (err) {
      setReferralMessage(err.message);
    } finally {
      setReferralLoading(false);
    }
  };

  const toggleReferralActive = async (referral) => {
    const nextActive = !referral.active;
    const confirmMsg = nextActive
      ? `Activate referral code ${referral.code}?`
      : `Deactivate referral code ${referral.code}?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setToggleBusyId(referral._id);
      const resp = await fetch(`/api/admin/referrals/${referral._id}`, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: authHeaders(true),
        body: JSON.stringify({ active: nextActive }),
      });
      const data = await readApiResponse(resp, 'Failed to update referral code.');
      setReferrals((prev) => prev.map((item) => (item._id === referral._id ? data.referral : item)));
    } catch (err) {
      setReferralMessage(err.message);
    } finally {
      setToggleBusyId(null);
    }
  };

  const createPayoutBatch = async (referral) => {
    try {
      setPayoutBusyCode(referral.code);
      const resp = await fetch('/api/admin/referrals/payout-batches/create', {
        method: 'POST',
        credentials: 'same-origin',
        headers: authHeaders(true),
        body: JSON.stringify({ referralCode: referral.code }),
      });
      const data = await readApiResponse(resp, 'Failed to create payout batch.');
      setReferralMessage(`Created payout batch for ${referral.code}: ${formatMoney(data.batch.totalAmount, data.batch.currency)}.`);
      await fetchData();
    } catch (err) {
      setReferralMessage(err.message);
    } finally {
      setPayoutBusyCode('');
    }
  };

  const markBatchPaid = async (batch) => {
    const payoutReference = window.prompt('Enter payout reference (optional):', batch.payoutReference || '');
    if (payoutReference === null) return;

    try {
      setMarkPaidBusyId(batch._id);
      const resp = await fetch(`/api/admin/referrals/payout-batches/${batch._id}/mark-paid`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: authHeaders(true),
        body: JSON.stringify({ payoutReference: payoutReference.trim() }),
      });
      const data = await readApiResponse(resp, 'Failed to mark payout as paid.');
      setReferralMessage(`Marked payout batch ${data.batch._id} as paid.`);
      await fetchData();
    } catch (err) {
      setReferralMessage(err.message);
    } finally {
      setMarkPaidBusyId('');
    }
  };

  const exportCsv = () => {
    if (!users || users.length === 0) return;
    const rows = [['id','email','admin','hasReferralDiscount','referralCodeUsed','referredByCreator','createdAt'], ...users.map(u=>[
      u._id,
      u.email,
      !!u.admin,
      !!u.hasReferralDiscount,
      u.referralCodeUsed || '',
      u.referredByCreator || '',
      u.createdAt,
    ])];
    const csv = rows.map(r=>r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'users.csv'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="page admin-users-page">
      <header className="page-header">
        <span className="page-eyebrow">Admin</span>
        <h1>Users</h1>
        <p className="page-subtitle">Manage user access, export user records, and grant or revoke admin roles.</p>
      </header>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="admin-actions">
        <button className="btn btn-outline-secondary" onClick={fetchData} disabled={loading}>{loading ? 'Loading...' : 'Refresh'}</button>
        <button className="btn btn-primary" onClick={exportCsv} disabled={users.length===0}>Export CSV</button>
      </div>

      <section className="panel admin-panel-surface mt-3">
        <h2 className="h5">Referral codes</h2>
        <p className="text-muted">Codes grant referred users a permanent 50% discount on the one-time ad removal purchase.</p>
        <form className="row g-2 mb-3" onSubmit={createReferralCode}>
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Code (e.g. CHEFJANE50)"
              value={referralCodeInput}
              onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
            />
          </div>
          <div className="col-md-5">
            <input
              type="text"
              className="form-control"
              placeholder="Creator name"
              value={creatorNameInput}
              onChange={(e) => setCreatorNameInput(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <button className="btn btn-primary w-100" type="submit" disabled={referralLoading}>
              {referralLoading ? 'Creating...' : 'Create code'}
            </button>
          </div>
        </form>
        {referralMessage && <div className="alert alert-info py-2">{referralMessage}</div>}

        {referrals.length === 0 ? (
          <div className="admin-empty">No referral codes yet.</div>
        ) : (
          <div className="table-responsive admin-table-wrap">
            <table className="table admin-table">
              <thead>
                <tr><th>Code</th><th>Creator</th><th>Registrations</th><th>Gross</th><th>Creator earnings</th><th>Unpaid</th><th>Available</th><th>Paid out</th><th>Status</th><th>Created</th><th /></tr>
              </thead>
              <tbody>
                {referrals.map((referral) => (
                  <tr key={referral._id}>
                    <td><code>{referral.code}</code></td>
                    <td>{referral.creatorName}</td>
                    <td>{Number.isFinite(referral.usageCount) ? referral.usageCount : 0}</td>
                    <td>{formatMoney(referral.grossRevenue || 0)}</td>
                    <td>{formatMoney(referral.creatorEarnings || 0)}</td>
                    <td>{formatMoney(referral.unpaidBalance || 0)}</td>
                    <td>{formatMoney(referral.availableForPayout || 0)}</td>
                    <td>{formatMoney(referral.paidOut || 0)}</td>
                    <td>
                      <span className={`badge ${referral.active ? 'bg-success' : 'bg-secondary'}`}>
                        {referral.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{new Date(referral.createdAt).toLocaleString()}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-success me-2"
                        onClick={() => createPayoutBatch(referral)}
                        disabled={payoutBusyCode === referral.code || !referral.active}
                        title="Create payout batch from available earnings"
                      >
                        {payoutBusyCode === referral.code ? 'Creating...' : 'Create payout'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => toggleReferralActive(referral)}
                        disabled={toggleBusyId === referral._id}
                      >
                        {referral.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel admin-panel-surface mt-3">
        <h2 className="h5">Payout batches</h2>
        {payoutBatches.length === 0 ? (
          <div className="admin-empty">No payout batches yet.</div>
        ) : (
          <div className="table-responsive admin-table-wrap">
            <table className="table admin-table">
              <thead>
                <tr><th>Batch</th><th>Code</th><th>Creator</th><th>Total</th><th>Items</th><th>Status</th><th>Created</th><th>Paid</th><th /></tr>
              </thead>
              <tbody>
                {payoutBatches.map((batch) => (
                  <tr key={batch._id}>
                    <td><code>{batch._id}</code></td>
                    <td><code>{batch.referralCode}</code></td>
                    <td>{batch.creatorName}</td>
                    <td>{formatMoney(batch.totalAmount || 0, batch.currency || 'usd')}</td>
                    <td>{batch.itemCount}</td>
                    <td>
                      <span className={`badge ${batch.status === 'paid' ? 'bg-success' : 'bg-warning text-dark'}`}>
                        {batch.status}
                      </span>
                    </td>
                    <td>{batch.createdAt ? new Date(batch.createdAt).toLocaleString() : '—'}</td>
                    <td>{batch.paidAt ? new Date(batch.paidAt).toLocaleString() : '—'}</td>
                    <td>
                      {batch.status !== 'paid' && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => markBatchPaid(batch)}
                          disabled={markPaidBusyId === batch._id}
                        >
                          {markPaidBusyId === batch._id ? 'Saving...' : 'Mark paid'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel admin-panel-surface mt-3">
        {loading ? <p>Loading users…</p> : (
          users.length === 0 ? <div className="admin-empty">No users available or endpoint not implemented.</div> : (
            <div className="table-responsive admin-table-wrap">
            <table className="table admin-table">
              <thead>
                <tr><th>Email</th><th>Admin</th><th>Referral</th><th>Created</th><th/></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id}>
                    <td>{u.email}</td>
                    <td>
                      <span className={`badge ${u.admin ? 'bg-success' : 'bg-secondary'}`}>{u.admin ? 'Admin' : 'User'}</span>
                    </td>
                    <td>
                      {u.hasReferralDiscount && u.referralCodeUsed
                        ? `${u.referralCodeUsed}${u.referredByCreator ? ` (${u.referredByCreator})` : ''}`
                        : '—'}
                    </td>
                    <td>{new Date(u.createdAt).toLocaleString()}</td>
                    <td><button className="btn btn-sm btn-outline-primary" onClick={() => toggleAdmin(u._id, !!u.admin)}>{u.admin ? 'Revoke' : 'Grant'}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )
        )}
      </section>
    </div>
  );
}

export default AdminUsers;
